/**
 * Container types, as the picker offers them.
 *
 * ⚠️  MIRRORS `backend/config/containerTypes.js`, code-for-code and in the same
 * order. The duplication is deliberate — a JS backend and a TS frontend share no
 * package — and it is the same arrangement the module registry uses. A code here
 * but not there can be picked and then refused by the API; a code there but not
 * here can never be picked at all.
 *
 * This is the carrier's published type-group table, complete, and nothing else.
 * The group is what a booking states ("three 40ft high cube reefers"); the
 * precise size type of the box that turns up is recorded per container in the
 * free-text `isoCode` field, because it is read off a door plate.
 *
 * Read the codes carefully: the first character is length (2 = 20ft, 4 = 40ft,
 * L = 45ft) and the second is height (2 = 8'6", 5 = 9'6"). So `45GP` is a *40ft*
 * high cube and `L5GP` is the 45ft one — which is exactly why every label spells
 * the size out in feet.
 */
export const CONTAINER_TYPE_OPTIONS = [
  // 20ft
  { value: "22GP", label: "22GP — 20ft General Purpose" },
  { value: "22VH", label: "22VH — 20ft Ventilated" },
  { value: "22UT", label: "22UT — 20ft Open Top" },
  { value: "22UP", label: "22UP — 20ft Hardtop" },
  { value: "20PF", label: "20PF — 20ft Flat, fixed ends (8ft high)" },
  { value: "22PF", label: "22PF — 20ft Flat, fixed ends" },
  { value: "22PC", label: "22PC — 20ft Flat, collapsible" },
  { value: "22RT", label: "22RT — 20ft Refrigerated" },
  { value: "20TD", label: "20TD — 20ft Tank, dangerous liquids (8ft high)" },
  { value: "22TN", label: "22TN — 20ft Tank, non-dangerous liquids" },
  { value: "22TD", label: "22TD — 20ft Tank, dangerous liquids" },

  // 40ft
  { value: "42GP", label: "42GP — 40ft General Purpose" },
  { value: "45GP", label: "45GP — 40ft High Cube General Purpose" },
  { value: "42UT", label: "42UT — 40ft Open Top" },
  { value: "45UT", label: "45UT — 40ft High Cube Open Top" },
  { value: "42UP", label: "42UP — 40ft Hardtop" },
  { value: "45UP", label: "45UP — 40ft High Cube Hardtop" },
  { value: "42PF", label: "42PF — 40ft Flat, fixed ends" },
  { value: "42PC", label: "42PC — 40ft Flat, collapsible" },
  { value: "45PC", label: "45PC — 40ft High Cube Flat, collapsible" },
  { value: "42RC", label: "42RC — 40ft Refrigerated, non-foodstuff" },
  { value: "45RT", label: "45RT — 40ft High Cube Refrigerated" },
  { value: "45RC", label: "45RC — 40ft High Cube Refrigerated, non-foodstuff" },

  // 45ft
  { value: "L5GP", label: "L5GP — 45ft High Cube General Purpose" },
];

/** Just the values, for the schema — one list, not two. */
export const CONTAINER_TYPE_VALUES = CONTAINER_TYPE_OPTIONS.map(
  (option) => option.value,
);

/**
 * Whether a container travels loaded or empty.
 *
 * Raw values only; the select's labels are translated at the call site
 * (`loadStatus.<value>`), the same way the directions below are.
 * Empty leads because it is the state a container is in for the longer part of
 * an outbound run — the commonest kind — and because a blank select and a
 * container nobody has described should not be one keystroke apart.
 */
export const CONTAINER_LOAD_STATUS_VALUES = ["empty", "full"] as const;

/**
 * Direction uses the shipment vocabulary (Outbound/Inbound) so a generated
 * shipment inherits the value verbatim. Labels are translated at the call site;
 * these are the raw values only.
 */
export const CONTAINER_ORDER_DIRECTION_VALUES = [
  "Outbound",
  "Inbound",
] as const;

/**
 * Attachment limits, mirroring what the API enforces (`uploadConfig.js`).
 *
 * Kept client-side as well so a file that the server would reject is caught
 * while the user is still looking at the picker — naming the file and the rule
 * it broke — rather than after they have filled in the whole form and pressed
 * Save. The server remains the authority; this is a courtesy, not a guarantee.
 */
export const ATTACHMENT_MAX_FILE_SIZE_MB = 10;
export const ATTACHMENT_MAX_FILE_SIZE =
  ATTACHMENT_MAX_FILE_SIZE_MB * 1024 * 1024;

/** Per drop zone, per save — general and customs each get their own budget. */
export const ATTACHMENT_MAX_FILES_PER_ZONE = 10;

/** Extensions the API's file filter accepts for order attachments. */
export const ATTACHMENT_ALLOWED_EXTENSIONS = [
  "jpeg",
  "jpg",
  "png",
  "gif",
  "pdf",
  "doc",
  "docx",
  "xml",
  "txt",
  "csv",
  "xls",
  "xlsx",
];

/** Tailwind classes per container status. */
export const CONTAINER_LINE_STATUS_STYLES: Record<string, string> = {
  // Paler than `planned`, and deliberately the quietest badge on the page: a
  // container nobody has released is the one state on this list where the
  // answer to "what is happening to this box" is "nothing yet".
  created: "bg-bone text-mist",
  planned: "bg-secondary-100 text-secondary-700",
  // The five in-flight statuses deepen through one hue rather than taking five
  // unrelated colours. They are a sequence, and reading them as one is the
  // whole point of splitting `in_progress` up: the shade says how far along,
  // the label says where. Five arbitrary colours would have to be memorised
  // instead of scanned.
  at_pickup_terminal: "bg-blue-50 text-blue-700",
  picked_up: "bg-blue-100 text-blue-800",
  // An added stop sits at whatever point of the route the dispatcher put it, so
  // it cannot take a shade from the sequence — it would claim a position it does
  // not have. Amber instead: off the scale on purpose, because "at a stop" is
  // the one status whose meaning is on the container rather than in the word.
  at_stop: "bg-amber-50 text-amber-800",
  stop_completed: "bg-amber-100 text-amber-900",
  at_loading_site: "bg-sky-100 text-sky-800",
  loaded: "bg-indigo-100 text-indigo-800",
  at_dropoff_terminal: "bg-violet-100 text-violet-800",
  // Only ever seen when something is scheduled after the drop-off. Shares the
  // drop-off's hue a shade deeper: it is the same terminal, one step on.
  returned: "bg-violet-200 text-violet-900",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-gray-100 text-gray-500",
};

/**
 * What an added stop can be for, and how it presents.
 *
 * The kind is a code so the data can be grouped by it — which containers went
 * through a weighbridge, which customs stops ran late — and the icon and accent
 * are how it is told apart at a glance on a route of eight cards. Labels are
 * keys into `containerOrders.route.stopKinds`, resolved at render.
 */
export const CUSTOM_STOP_KIND_VALUES = [
  "customs",
  "weighing",
  "extraLoading",
  "waypoint",
  "other",
] as const;

/**
 * Packaging types a container's cargo may be counted in.
 *
 * Kept here rather than reaching for the shipment form's `PACKAGE_TYPES`:
 * container orders are their own module, and the two lists happen to overlap
 * rather than being the same list. This one matches the model's enum exactly —
 * `PACKAGE_TYPES` is missing CRATE, which the API accepts.
 *
 * Labels are keys into `containerOrders.lineCargo.types`, resolved at render
 * so the dropdown follows the user's language like every other one.
 */
export const CONTAINER_CARGO_TYPES = [
  "EUR",
  "BOX",
  "PLT",
  "PCS",
  "CTN",
  "BAG",
  "DRM",
  "CYL",
  "CRATE",
  "FTL",
  "LTL",
  "OTHER",
] as const;
